import {
    Configuration,
    UserSubscriptionApi,
    Pay,
    CreateUserSubscription,
    UserSubscriptionReadOnly,
    HttpMethod,
    RequestContext,
} from 'hotdesk_typescript';
import { getCookie } from 'cookies-next/client';

type AutoRenewPayload = {
    userSubscriptions: number[];
    autoRenew: boolean;
};

/** Body for POST /user_subscription/renew/ (same shape as pay). */
export type SubscriptionRenewPayload = {
    creditCardId: string | number;
    customerId: string | number;
    subscriptionId: number;
    /** The member's auto-renew choice for the membership this creates. */
    autoRenew?: boolean;
};

/** Body for Apple/Google Pay renew init. */
export type SubscriptionRenewInitPayload = {
    subscriptionId: number;
    autoRenew?: boolean;
};

export type SubscriptionMobilePayInitResponse = {
    clientSecret?: string;
    amount?: number;
    currency?: string;
    userSubscriptionId?: number;
    transactionId?: number;
};

const buildToggleBodies = (data: AutoRenewPayload): Record<string, unknown>[] => [
    {
        userSubscriptions: data.userSubscriptions,
        autoRenew: data.autoRenew,
    },
    {
        user_subscriptions: data.userSubscriptions,
        auto_renew: data.autoRenew,
    },
];

const parseErrorMessage = (text: string): string | null => {
    if (!text.trim()) return null;
    try {
        const json = JSON.parse(text) as Record<string, unknown>;
        if (typeof json.detail === 'string') return json.detail;
        if (typeof json.message === 'string') return json.message;
        const firstKey = Object.keys(json)[0];
        const firstValue = json[firstKey];
        if (typeof firstValue === 'string') return firstValue;
        if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') {
            return firstValue[0];
        }
    } catch {
        return text.slice(0, 200);
    }
    return null;
};

export class SubscriptionsService {
    private UserSubscriptionApi!: UserSubscriptionApi;
    private apiConfig: Configuration;

    constructor(apiConfig: Configuration) {
        this.apiConfig = apiConfig;
        this.UserSubscriptionApi = new UserSubscriptionApi(apiConfig);
    }

    getUserSubscription(listingId: string, userId: string) {
        return this.UserSubscriptionApi.getUserSubscriptionForListing(
            listingId,
            userId
        );
    }
    createUserSubscription(data: CreateUserSubscription): Promise<UserSubscriptionReadOnly> {
        try {
            return this.UserSubscriptionApi.createUserSubscription(data);
        } catch (error) {
            throw error;
        }
    }
    payUserSubscription(data: Pay): Promise<Pay> {
        try {
            return this.UserSubscriptionApi.pay(data);
        } catch (error) {
            throw error;
        }
    }

    userSubscriptionApplePayInit(data: { subscriptionId: number }): Promise<any> {
        try {
            return this.UserSubscriptionApi.userSubscriptionApplePayInit(data);
        } catch (error) {
            throw error;
        }
    }

    userSubscriptionGooglePayInit(data: { subscriptionId: number }): Promise<any> {
        try {
            return this.UserSubscriptionApi.userSubscriptionGooglePayInit(data);
        } catch (error) {
            throw error;
        }
    }

    private async buildAuthenticatedJsonRequest(
        path: string,
        method: HttpMethod,
        languageCode: string,
        body: Record<string, unknown>
    ): Promise<RequestContext> {
        const requestContext = this.apiConfig.baseServer.makeRequestContext(path, method);
        requestContext.setHeaderParam('Accept', 'application/json, */*;q=0.8');
        requestContext.setHeaderParam('Content-Type', 'application/json');
        requestContext.setQueryParam('lang', languageCode);
        requestContext.setBody(JSON.stringify(body));

        const bearer = this.apiConfig.authMethods.Bearer;
        if (bearer) {
            await bearer.applySecurityAuthentication(requestContext);
        } else {
            const accessToken = getCookie('accessToken');
            const csrfToken = getCookie('ga');
            if (accessToken) {
                requestContext.setHeaderParam('Authorization', `Token ${accessToken}`);
            }
            if (csrfToken) {
                requestContext.setHeaderParam('X-CSRFToken', String(csrfToken));
            }
        }

        return requestContext;
    }

    private async buildAutoRenewRequest(
        languageCode: string,
        body: Record<string, unknown>
    ): Promise<RequestContext> {
        return this.buildAuthenticatedJsonRequest(
            '/user_subscription/auto_renew_toggle/',
            HttpMethod.PUT,
            languageCode,
            body
        );
    }

    private async putJson(
        path: string,
        languageCode: string,
        body: Record<string, unknown>
    ): Promise<void> {
        const requestContext = await this.buildAuthenticatedJsonRequest(
            path,
            HttpMethod.PUT,
            languageCode,
            body
        );
        const response = await fetch(requestContext.getUrl(), {
            method: 'PUT',
            credentials: 'omit',
            headers: requestContext.getHeaders(),
            body: requestContext.getBody() as string,
        });

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(parseErrorMessage(text) ?? `HTTP ${response.status}`);
        }
    }

    /**
     * Mark the package a membership should move onto — the step that turns a
     * renewal into an upgrade.
     *
     * `/renew/` reads this to decide which package the replacement lands on and
     * which Stripe price to charge, so it has to be set *before* renewing.
     * Without it the renewal simply repeats the package the member already has,
     * at the price they already pay.
     */
    updateSubscriptionDesired(
        data: { userSubscriptionId: number; subscriptionId: number; packageId: number },
        languageCode: string
    ): Promise<void> {
        return this.putJson(
            `/user_subscription/${data.userSubscriptionId}/update_subscription_desired/`,
            languageCode,
            {
                subscriptionDesired: {
                    subscription: data.subscriptionId,
                    package: data.packageId,
                },
            }
        );
    }

    private async postJson<T>(
        path: string,
        languageCode: string,
        body: Record<string, unknown>,
        fallbackError: string
    ): Promise<T> {
        const requestContext = await this.buildAuthenticatedJsonRequest(
            path,
            HttpMethod.POST,
            languageCode,
            body
        );
        const response = await fetch(requestContext.getUrl(), {
            method: 'POST',
            credentials: 'omit',
            headers: requestContext.getHeaders(),
            body: requestContext.getBody() as string,
        });

        const text = await response.text().catch(() => '');
        if (!response.ok) {
            throw new Error(parseErrorMessage(text) ?? `HTTP ${response.status}`);
        }

        if (!text.trim()) {
            throw new Error(fallbackError);
        }

        try {
            return JSON.parse(text) as T;
        } catch {
            throw new Error(fallbackError);
        }
    }

    /** Renew via credit card — POST /user_subscription/renew/ */
    renewUserSubscription(
        data: SubscriptionRenewPayload,
        languageCode: string
    ): Promise<UserSubscriptionReadOnly> {
        return this.postJson<UserSubscriptionReadOnly>(
            '/user_subscription/renew/',
            languageCode,
            {
                creditCardId: String(data.creditCardId),
                customerId: String(data.customerId),
                subscriptionId: data.subscriptionId,
                ...(typeof data.autoRenew === 'boolean' ? { autoRenew: data.autoRenew } : {}),
            },
            'Failed to renew membership'
        );
    }

    userSubscriptionApplePayRenewInit(
        data: SubscriptionRenewInitPayload,
        languageCode: string
    ): Promise<SubscriptionMobilePayInitResponse> {
        return this.postJson<SubscriptionMobilePayInitResponse>(
            '/user_subscription/apple_pay_renew_init/',
            languageCode,
            {
                subscriptionId: data.subscriptionId,
                ...(typeof data.autoRenew === 'boolean' ? { autoRenew: data.autoRenew } : {}),
            },
            'Failed to initiate Apple Pay renewal'
        );
    }

    userSubscriptionGooglePayRenewInit(
        data: SubscriptionRenewInitPayload,
        languageCode: string
    ): Promise<SubscriptionMobilePayInitResponse> {
        return this.postJson<SubscriptionMobilePayInitResponse>(
            '/user_subscription/google_pay_renew_init/',
            languageCode,
            {
                subscriptionId: data.subscriptionId,
                ...(typeof data.autoRenew === 'boolean' ? { autoRenew: data.autoRenew } : {}),
            },
            'Failed to initiate Google Pay renewal'
        );
    }

    async toggleAutoRenew(data: AutoRenewPayload, languageCode: string): Promise<void> {
        const ids = data.userSubscriptions.filter((id) => Number.isFinite(id) && id > 0);
        if (!ids.length) {
            throw new Error('No valid user membership ids');
        }

        const payload = { ...data, userSubscriptions: ids };
        const bodies = buildToggleBodies(payload);
        let lastError: string | null = null;

        for (const body of bodies) {
            const requestContext = await this.buildAutoRenewRequest(languageCode, body);
            const response = await fetch(requestContext.getUrl(), {
                method: 'PUT',
                credentials: 'omit',
                headers: requestContext.getHeaders(),
                body: requestContext.getBody() as string,
            });

            if (response.ok) {
                return;
            }

            const text = await response.text().catch(() => '');
            lastError = parseErrorMessage(text) ?? `HTTP ${response.status}`;

            // Only retry alternate body shape on validation errors.
            if (response.status !== 400) {
                break;
            }
        }

        throw new Error(lastError ?? 'Failed to update auto renewal');
    }
}
